import React, { useState } from "react";

const Sparkline = ({
  data,
  width = 200,
  height = 50,
  showTooltip = true,
  valueFunction = null,
  tooltipBelow = false,
  green = "#2ecc71",
  red = "#e74c3c",
  flat = "#ecf0f1",
}) => {
  const [hoverX, setHoverX] = useState(null);

  if (!data || data.length === 0) return null;

  const GREEN = green;
  const RED = red;
  const FLAT = flat;

  const validPoints = data
    .map(([label, val], i) => (val != null ? { val, label, i } : null))
    .filter((p) => p != null);

  if (validPoints.length < 2) return null;

  const values = validPoints.map((p) => p.val);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);

  const xStep = width / (data.length - 1);
  const yScale = maxVal - minVal === 0 ? 1 : maxVal - minVal;

  const points = validPoints.map(({ val, label, i }) => {
    const x = i * xStep;
    const y = height - ((val - minVal) / yScale) * height;
    return { x, y, val, label, i };
  });

  const interpolateColor = (t) => {
    const lerp = (a, b, t) => a + (b - a) * t;
    const hexToRgb = (hex) => {
      const bigint = parseInt(hex.slice(1), 16);
      return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
      };
    };
    const rgbToHex = ({ r, g, b }) =>
      `#${[r, g, b]
        .map((x) => x.toString(16).padStart(2, "0"))
        .join("")}`;
  
    const colorRed = hexToRgb(RED);
    const colorGreen = hexToRgb(GREEN);
    const colorFlat = hexToRgb(FLAT);
  
    // Clamp t between 0 and 1
    t = Math.min(1, Math.max(0, t));
  
    if (t < 0.5) {
      // Interpolate between Red and Flat (0 -> red, 0.5 -> flat)
      const nt = t / 0.5;
      return rgbToHex({
        r: Math.round(lerp(colorRed.r, colorFlat.r, nt)),
        g: Math.round(lerp(colorRed.g, colorFlat.g, nt)),
        b: Math.round(lerp(colorRed.b, colorFlat.b, nt)),
      });
    } else {
      // Interpolate between Flat and Green (0.5 -> flat, 1 -> green)
      const nt = (t - 0.5) / 0.5;
      return rgbToHex({
        r: Math.round(lerp(colorFlat.r, colorGreen.r, nt)),
        g: Math.round(lerp(colorFlat.g, colorGreen.g, nt)),
        b: Math.round(lerp(colorFlat.b, colorGreen.b, nt)),
      });
    }
  };

  const segments = [];
  for (let i = 1; i < points.length; i++) {
    const delta = points[i].val - points[i - 1].val;
    const range = maxVal - minVal || 1;
    const t = Math.max(0, Math.min(1, 0.5 + (delta / range) * 2)); // normalize to 0..1
    segments.push({
      x1: points[i - 1].x,
      y1: points[i - 1].y,
      x2: points[i].x,
      y2: points[i].y,
      color: interpolateColor(t),
    });
  }

  const handleMouseMove = (e) => {
    if (!showTooltip) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const closestIndex = Math.round(mouseX / xStep);
    if (closestIndex >= 0 && closestIndex < data.length) {
      const point = points.find((p) => p.i === closestIndex);
      if (point) setHoverX(point);
    }
  };

  return (
    <div style={{ position: "relative", width }}>
      {hoverX && (
        <div
          style={{
            position: "absolute",
            left: hoverX.x - 40,
            // bottom: height + 10,
            top: tooltipBelow ? height + 10 : '',
            bottom: tooltipBelow ? '' : height + 10,
            width: 80,
            background: "#ecf0f1",
            color: "black",
            textAlign: "center",
            padding: "2px 4px",
            borderRadius: 4,
            fontSize: 11,
            pointerEvents: "none",
            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
            zIndex: 100,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: "bold", opacity: 0.6 }}>{hoverX.label}</div>
          <div style={{ fontSize: 13 }}>{valueFunction ? valueFunction(hoverX.val) : hoverX.val}</div>
        </div>
      )}
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        onMouseLeave={() => setHoverX(null)}
        onMouseMove={handleMouseMove}
        style={{ overflow: "visible" }}
      >
        {segments.map((seg, idx) => (
          <line
            key={idx}
            x1={seg.x1}
            y1={seg.y1}
            x2={seg.x2}
            y2={seg.y2}
            stroke={seg.color}
            strokeWidth="2"
          />
        ))}

        {hoverX && (
          <>
            <line
              x1={hoverX.x}
              y1={0}
              x2={hoverX.x}
              y2={height}
              stroke="white"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
            <circle
              cx={hoverX.x}
              cy={hoverX.y}
              r={4}
              fill="white"
              style={{ transition: "r 0.2s ease-in-out, transform 0.2s ease-in-out" }}
            />
          </>
        )}
      </svg>
    </div>
  );
};

export default Sparkline;